import { useState, useMemo } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    Search, Filter, Download, Eye, X, Monitor, Smartphone, Tablet,
    MapPin, Globe, Clock, Shield, UserCheck, UserX, AlertTriangle,
    ChevronDown, ChevronUp, Users, Activity
} from 'lucide-react'
import { mockLoginData, type LoginRecord } from '@/data/admin-mock-data'
import { motion, AnimatePresence } from 'framer-motion'

type RoleFilter = 'all' | 'student' | 'mentor' | 'counsellor' | 'admin' | 'sub-admin'
type StatusFilter = 'all' | 'online' | 'offline' | 'idle'
type AccountFilter = 'all' | 'active' | 'suspended' | 'deactivated'
type SortField = 'name' | 'lastLogin' | 'loginCount' | 'role'
type SortDirection = 'asc' | 'desc'

export function AdminLoginDataView() {
    const [loginRecords] = useState<LoginRecord[]>(mockLoginData)
    const [searchQuery, setSearchQuery] = useState('')
    const [roleFilter, setRoleFilter] = useState<RoleFilter>('all')
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
    const [accountFilter, setAccountFilter] = useState<AccountFilter>('all')
    const [sortField, setSortField] = useState<SortField>('lastLogin')
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
    const [selectedUser, setSelectedUser] = useState<LoginRecord | null>(null)
    const [showFilters, setShowFilters] = useState(false)

    const filteredAndSorted = useMemo(() => {
        let filtered = loginRecords.filter(record => {
            const matchesSearch = record.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                record.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                record.userId.toLowerCase().includes(searchQuery.toLowerCase())
            const matchesRole = roleFilter === 'all' || record.role === roleFilter
            const matchesStatus = statusFilter === 'all' || record.status === statusFilter
            const matchesAccount = accountFilter === 'all' || record.accountStatus === accountFilter
            return matchesSearch && matchesRole && matchesStatus && matchesAccount
        })

        filtered.sort((a, b) => {
            let comparison = 0
            switch (sortField) {
                case 'name': comparison = a.name.localeCompare(b.name); break
                case 'lastLogin': comparison = new Date(a.lastLogin).getTime() - new Date(b.lastLogin).getTime(); break
                case 'loginCount': comparison = a.loginCount - b.loginCount; break
                case 'role': comparison = a.role.localeCompare(b.role); break
            }
            return sortDirection === 'desc' ? -comparison : comparison
        })

        return filtered
    }, [loginRecords, searchQuery, roleFilter, statusFilter, accountFilter, sortField, sortDirection])

    const stats = useMemo(() => ({
        total: loginRecords.length,
        online: loginRecords.filter(r => r.status === 'online').length,
        idle: loginRecords.filter(r => r.status === 'idle').length,
        offline: loginRecords.filter(r => r.status === 'offline').length,
    }), [loginRecords])

    const toggleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('desc')
        }
    }

    const SortIcon = ({ field }: { field: SortField }) => {
        if (sortField !== field) return null
        return sortDirection === 'asc'
            ? <ChevronUp className="h-3 w-3 inline ml-1" />
            : <ChevronDown className="h-3 w-3 inline ml-1" />
    }

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'online': return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />Online</span>
            case 'idle': return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"><span className="w-2 h-2 rounded-full bg-amber-500" />Idle</span>
            case 'offline': return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"><span className="w-2 h-2 rounded-full bg-slate-400" />Offline</span>
            default: return null
        }
    }

    const getAccountBadge = (status: string) => {
        switch (status) {
            case 'active': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Active</span>
            case 'suspended': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">Suspended</span>
            case 'deactivated': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Deactivated</span>
            default: return null
        }
    }

    const getRoleBadge = (role: string) => {
        const colors: Record<string, string> = {
            'student': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
            'mentor': 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400',
            'counsellor': 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400',
            'admin': 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400',
            'sub-admin': 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400',
        }
        return <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colors[role] || 'bg-slate-100 text-slate-700'}`}>{role.replace('-', ' ')}</span>
    }

    const getDeviceIcon = (device: string) => {
        if (device.toLowerCase().includes('phone') || device.toLowerCase().includes('iphone') || device.toLowerCase().includes('android')) return <Smartphone className="h-4 w-4" />
        if (device.toLowerCase().includes('ipad') || device.toLowerCase().includes('tablet')) return <Tablet className="h-4 w-4" />
        return <Monitor className="h-4 w-4" />
    }

    const formatDateTime = (dateStr: string) => {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' + date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
    }

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div>
                <h2 className="text-2xl font-bold text-theme-primary">Login Data</h2>
                <p className="text-sm text-theme-secondary">Monitor all user login activity, sessions, and account status</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                            <Users className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-theme-primary">{stats.total}</p>
                            <p className="text-xs text-theme-secondary">Total Users</p>
                        </div>
                    </div>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                            <Activity className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-emerald-600">{stats.online}</p>
                            <p className="text-xs text-theme-secondary">Online Now</p>
                        </div>
                    </div>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                            <Clock className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-amber-600">{stats.idle}</p>
                            <p className="text-xs text-theme-secondary">Idle</p>
                        </div>
                    </div>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center">
                            <UserX className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-slate-600">{stats.offline}</p>
                            <p className="text-xs text-theme-secondary">Offline</p>
                        </div>
                    </div>
                </GlassmorphicCard>
            </div>

            {/* Search, Filters & Export */}
            <GlassmorphicCard className="p-4">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col md:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-secondary" />
                            <input
                                type="text"
                                placeholder="Search by name, email, or user ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors ${showFilters ? 'bg-primary-blue text-white border-primary-blue' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                            >
                                <Filter className="h-4 w-4" />
                                <span className="text-sm font-medium">Filters</span>
                            </button>
                            <button className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-secondary rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                                <Download className="h-4 w-4" />
                                <span className="text-sm font-medium hidden sm:inline">Export</span>
                            </button>
                        </div>
                    </div>

                    <AnimatePresence>
                        {showFilters && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                                    <div>
                                        <label className="block text-xs font-medium text-theme-secondary mb-1.5">Role</label>
                                        <select
                                            value={roleFilter}
                                            onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
                                            className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                        >
                                            <option value="all">All Roles</option>
                                            <option value="student">Student</option>
                                            <option value="mentor">Mentor</option>
                                            <option value="counsellor">Counsellor</option>
                                            <option value="admin">Admin</option>
                                            <option value="sub-admin">Sub Admin</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-theme-secondary mb-1.5">Status</label>
                                        <select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                                            className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                        >
                                            <option value="all">All Status</option>
                                            <option value="online">Online</option>
                                            <option value="idle">Idle</option>
                                            <option value="offline">Offline</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-theme-secondary mb-1.5">Account</label>
                                        <select
                                            value={accountFilter}
                                            onChange={(e) => setAccountFilter(e.target.value as AccountFilter)}
                                            className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                        >
                                            <option value="all">All Accounts</option>
                                            <option value="active">Active</option>
                                            <option value="suspended">Suspended</option>
                                            <option value="deactivated">Deactivated</option>
                                        </select>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </GlassmorphicCard>

            {/* Data Table */}
            <GlassmorphicCard className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                                <th className="p-4 text-xs font-semibold text-theme-secondary uppercase cursor-pointer hover:text-primary-blue transition-colors" onClick={() => toggleSort('name')}>
                                    User <SortIcon field="name" />
                                </th>
                                <th className="p-4 text-xs font-semibold text-theme-secondary uppercase cursor-pointer hover:text-primary-blue transition-colors" onClick={() => toggleSort('role')}>
                                    Role <SortIcon field="role" />
                                </th>
                                <th className="p-4 text-xs font-semibold text-theme-secondary uppercase">Status</th>
                                <th className="p-4 text-xs font-semibold text-theme-secondary uppercase cursor-pointer hover:text-primary-blue transition-colors" onClick={() => toggleSort('lastLogin')}>
                                    Last Login <SortIcon field="lastLogin" />
                                </th>
                                <th className="p-4 text-xs font-semibold text-theme-secondary uppercase cursor-pointer hover:text-primary-blue transition-colors hidden md:table-cell" onClick={() => toggleSort('loginCount')}>
                                    Logins <SortIcon field="loginCount" />
                                </th>
                                <th className="p-4 text-xs font-semibold text-theme-secondary uppercase hidden lg:table-cell">Device</th>
                                <th className="p-4 text-xs font-semibold text-theme-secondary uppercase hidden xl:table-cell">Location</th>
                                <th className="p-4 text-xs font-semibold text-theme-secondary uppercase text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {filteredAndSorted.map((record) => (
                                <tr key={record.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-blue to-violet-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                                {record.name.charAt(0)}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-semibold text-theme-primary text-sm truncate">{record.name}</div>
                                                <div className="text-xs text-theme-secondary truncate">{record.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4">{getRoleBadge(record.role)}</td>
                                    <td className="p-4">{getStatusBadge(record.status)}</td>
                                    <td className="p-4 text-sm text-theme-secondary whitespace-nowrap">{formatDateTime(record.lastLogin)}</td>
                                    <td className="p-4 hidden md:table-cell">
                                        <span className="font-semibold text-theme-primary">{record.loginCount}</span>
                                    </td>
                                    <td className="p-4 hidden lg:table-cell">
                                        <div className="flex items-center gap-2 text-sm text-theme-secondary">
                                            {getDeviceIcon(record.device)}
                                            <span className="truncate max-w-[120px]">{record.device}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 hidden xl:table-cell">
                                        <div className="flex items-center gap-1.5 text-sm text-theme-secondary">
                                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                                            <span className="truncate">{record.location}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-right">
                                        <button
                                            onClick={() => setSelectedUser(record)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-blue bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                                        >
                                            <Eye className="h-3.5 w-3.5" />
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {filteredAndSorted.length === 0 && (
                    <div className="p-12 text-center">
                        <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-theme-secondary font-medium">No records found</p>
                        <p className="text-sm text-theme-muted mt-1">Try adjusting your search or filters</p>
                    </div>
                )}
                <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30">
                    <p className="text-sm text-theme-secondary">
                        Showing <span className="font-semibold text-theme-primary">{filteredAndSorted.length}</span> of <span className="font-semibold text-theme-primary">{loginRecords.length}</span> records
                    </p>
                </div>
            </GlassmorphicCard>

            {/* User Detail Modal */}
            <AnimatePresence>
                {selectedUser && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedUser(null)}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            onClick={e => e.stopPropagation()}
                            className="w-full max-w-lg"
                        >
                            <GlassmorphicCard className="p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
                                <div className="flex items-start justify-between mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-blue to-violet-600 flex items-center justify-center text-white font-bold text-xl">
                                            {selectedUser.name.charAt(0)}
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-theme-primary">{selectedUser.name}</h3>
                                            <p className="text-sm text-theme-secondary">{selectedUser.email}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                {getRoleBadge(selectedUser.role)}
                                                {getAccountBadge(selectedUser.accountStatus)}
                                            </div>
                                        </div>
                                    </div>
                                    <button onClick={() => setSelectedUser(null)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                        <X className="h-5 w-5 text-theme-secondary" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {/* Session Info */}
                                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl space-y-3">
                                        <h4 className="text-sm font-semibold text-theme-primary flex items-center gap-2">
                                            <Activity className="h-4 w-4 text-primary-blue" /> Session Info
                                        </h4>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <p className="text-xs text-theme-secondary">Current Status</p>
                                                <div className="mt-1">{getStatusBadge(selectedUser.status)}</div>
                                            </div>
                                            <div>
                                                <p className="text-xs text-theme-secondary">Total Logins</p>
                                                <p className="font-bold text-theme-primary text-lg">{selectedUser.loginCount}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-theme-secondary">Last Login</p>
                                                <p className="text-sm font-medium text-theme-primary">{formatDateTime(selectedUser.lastLogin)}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-theme-secondary">Member Since</p>
                                                <p className="text-sm font-medium text-theme-primary">{new Date(selectedUser.joinedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Device & Location */}
                                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl space-y-3">
                                        <h4 className="text-sm font-semibold text-theme-primary flex items-center gap-2">
                                            <Monitor className="h-4 w-4 text-primary-blue" /> Device & Location
                                        </h4>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <p className="text-xs text-theme-secondary">Device</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    {getDeviceIcon(selectedUser.device)}
                                                    <p className="text-sm font-medium text-theme-primary">{selectedUser.device}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-xs text-theme-secondary">Browser</p>
                                                <p className="text-sm font-medium text-theme-primary mt-1">{selectedUser.browser}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-theme-secondary">IP Address</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Globe className="h-3.5 w-3.5 text-theme-secondary" />
                                                    <p className="text-sm font-mono text-theme-primary">{selectedUser.ipAddress}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-xs text-theme-secondary">Location</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <MapPin className="h-3.5 w-3.5 text-theme-secondary" />
                                                    <p className="text-sm font-medium text-theme-primary">{selectedUser.location}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Account Actions */}
                                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl space-y-3">
                                        <h4 className="text-sm font-semibold text-theme-primary flex items-center gap-2">
                                            <Shield className="h-4 w-4 text-primary-blue" /> Account Actions
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedUser.accountStatus === 'active' ? (
                                                <>
                                                    <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors">
                                                        <AlertTriangle className="h-3.5 w-3.5" /> Suspend Account
                                                    </button>
                                                    <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors">
                                                        <UserX className="h-3.5 w-3.5" /> Deactivate
                                                    </button>
                                                </>
                                            ) : (
                                                <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors">
                                                    <UserCheck className="h-3.5 w-3.5" /> Reactivate Account
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    )
}
